import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { typeOfAgentEmail } from '@amfam/policy/add-vehicle/data-access';
import { PolicySelectors } from '@amfam/policy/data-access';
import { VehicleFacade, vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { Agent, agentQuery } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions, routerQuery } from '@amfam/shared/utility/navigation';
import { CopyService, ENVIRONMENT, EnvironmentBase } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  DsModalService,
  ErrorLevels,
  ErrorObj,
  PageTitleService
} from '@amfam/ui-kit';

import * as VehicleFeatureActions from '../+state/add-vehicle-feature.actions';
import { addVehicleFeatureQuery } from '../+state/add-vehicle-feature.selectors';
import { AddVehicleFeatureService } from '../services/add-vehicle-feature.service';

@Component({
  selector: 'ds-add-vehicle-container',
  templateUrl: './add-vehicle-container.component.html',
  styleUrls: ['./add-vehicle-container.component.scss'],
  standalone: false
})
export class AddVehicleContainerComponent implements OnInit, OnDestroy {
  public exitFlowModalId = this.modalService.createModalId('exitFlowModalId');
  public earlyExitFlowModalId = this.modalService.createModalId('earlyExitFlowModalId');
  public agentSelectionModalId = this.modalService.createModalId('agentSelectionModalId');
  public agents$ = this.store.select(agentQuery.getRealAgents);

  public isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  public errorList: Array<ErrorObj> = [this.getPoliciesError(this.copyService)];
  public hasError: boolean;
  public classicOnlyAutoPolicyOrCompOnlyAdv = false;
  public exitFlowConfimationText;
  public exitPrimaryButtonText = this.copyService.getCopy(
    'policy.addVehicleExitFlowPrimaryButtonText'
  );
  public exitFlowConfimationTextPartner;

  private stop$: Subject<void> = new Subject<void>();
  private addVehicleProcessExited: boolean;
  private addVehicleText = this.copyService.getCopy('policy.addVehicle');

  private policySub: Subscription;

  private policyProducingAgents$ = this.store.select(
    addVehicleFeatureQuery.selectAdvanceAutoPolicyProducingAgents
  );
  private policyClassicAgents$ = this.store.select(
    addVehicleFeatureQuery.selectClassicAutoPolicyProducingAgents
  );

  constructor(
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private copyService: CopyService,
    private router: Router,
    private modalService: DsModalService,
    private store: Store,
    private addVehicleService: AddVehicleFeatureService,
    private vehicleFacade: VehicleFacade,
    private analyticsFacade: AnalyticsFacade,
    private featureFlagService: FeatureFlagService,
    @Inject(ENVIRONMENT) private environment: EnvironmentBase,
    private route: ActivatedRoute
  ) {
    this.addVehicleService.environment = environment;
  }

  ngOnInit(): void {
    this.isPartner$.subscribe(isPartner => {
      this.exitFlowConfimationText = isPartner
        ? this.copyService.getCopy('policy.addVehicleExitFlowConfimationTextPartner')
        : this.copyService.getCopy('policy.addVehicleExitFlowConfimationText');
    });
    this.pageTitle.set(this.addVehicleText);
    this.dockingBarService.registerHeading(this.addVehicleText);
    // Reset Add Vehicle state to start flow
    this.store.dispatch(VehicleFeatureActions.addVehicleReset());
    // sets the appropriate docking bar heading on navigation

    this.router.events
      .pipe(
        filter(routerEvent => routerEvent instanceof NavigationEnd),
        takeUntil(this.stop$)
      )
      .subscribe(() => {
        this.dockingBarService.registerHeading(this.addVehicleText);
      });

    this.policySub = combineLatest([
      this.store.select(vehicleQuery.selectNewVehicle),
      this.store.select(BrandSelectors.selectIsPartner),
      this.store.select(PolicySelectors.selectHasClassicAutoPolicies),
      this.store.select(PolicySelectors.selectHasAdvanceAutoPolicies),
      this.store.select(PolicySelectors.selectHasOnlyPolicyComprehensiveCoverage)
    ]).subscribe(
      ([
        newVehicle,
        isPartner,
        hasClassicAutoPolicy,
        hasAdvanceAutoPolicy,
        hasOnlyPolicyComprehensiveCoverage
      ]) => {
        if (newVehicle) {
          const advanceCompOnly =
            hasAdvanceAutoPolicy && !isPartner && hasOnlyPolicyComprehensiveCoverage;

          const classicOnlyAutoPolicy = hasClassicAutoPolicy && !hasAdvanceAutoPolicy && !isPartner;

          if (advanceCompOnly || classicOnlyAutoPolicy) {
            this.classicOnlyAutoPolicyOrCompOnlyAdv = true;
          }
        }
      }
    );
  }

  onActivate(componentReference) {
    if (_get(componentReference, 'cancelEvent')) {
      //Below will subscribe to the cancel emitter
      componentReference.cancelEvent.pipe(takeUntil(this.stop$)).subscribe(cancel => {
        if (cancel) {
          this.modalService.open(this.exitFlowModalId);
        }
      });
    }

    if (_get(componentReference, 'exitEvent')) {
      //Below will subscribe to the exit emitter
      componentReference.exitEvent.pipe(takeUntil(this.stop$)).subscribe(exit => {
        if (exit) {
          this.modalService.open(this.earlyExitFlowModalId);
        }
      });
    }

    if (_get(componentReference, 'notifyAgentEvent')) {
      //Below will subscribe to the exit emitter
      componentReference.notifyAgentEvent.pipe(takeUntil(this.stop$)).subscribe(payload => {
        if (payload.agent) {
          this.agentConfirmationFromModal(payload.agent);
        } else {
          this.sendToAgent(payload.isProcessExited);
        }
      });
    }

    this.handleError(componentReference);
  }

  sendToAgent(addVehicleProcessExited?: boolean) {
    this.addVehicleProcessExited = !!addVehicleProcessExited;

    combineLatest([this.policyProducingAgents$, this.isPartner$, this.policyClassicAgents$])
      .pipe(take(1))
      .subscribe(([agents, isPartner, classicAgent]) => {
        if (!isPartner) {
          if (agents.length > 1) {
            this.modalService.open(this.agentSelectionModalId);
          } else {
            if (agents.length === 0) {
              this.agentConfirmationFromModal(classicAgent[0]);
            } else {
              this.agentConfirmationFromModal(agents[0]);
            }
          }
        } else {
          this.partnerConfirmationFromModal();
        }
      });

    this.analyticsFacade.trackEvent({
      event: 'agent_referral',
      form_name: 'Add Vehicle'
    });
  }

  agentConfirmationFromModal(agent?: Agent) {
    this.addVehicleService
      .typeOfAgentEmailBeingSent(this.addVehicleProcessExited)
      .pipe(take(1))
      .subscribe((type: typeOfAgentEmail) => {
        this.store.dispatch(
          VehicleFeatureActions.addVehicleSendNotification({
            payload: {
              agent: agent,
              typeOfEmail: type,
              classicOnlyAutoPolicyOrCompOnlyAdv: this.classicOnlyAutoPolicyOrCompOnlyAdv
            }
          })
        );
        this.modalService.close(this.agentSelectionModalId);
      });
  }

  partnerConfirmationFromModal() {
    this.addVehicleService
      .typeOfPartnerEmailBeingSent(this.addVehicleProcessExited)
      .pipe(take(1))
      .subscribe((type: typeOfAgentEmail) => {
        this.store.dispatch(
          VehicleFeatureActions.addVehicleSendPartnerNotification({
            payload: { typeOfEmail: type }
          })
        );
        this.modalService.close(this.agentSelectionModalId);
      });
  }

  stayFromExitModal() {
    this.dockingBarService.registerHeading(this.addVehicleText);
  }

  navigateToOverview() {
    this.store.dispatch(VehicleFeatureActions.addVehicleExitFlow());

    combineLatest([
      this.store.select(routerQuery.getRouterCurrentStateUrl),
      this.vehicleFacade.vehiclePolicyChangeEntryPoint$
    ])
      .pipe(
        map(([url, entryPoint]) => ({
          url,
          entryPoint
        })),
        take(1)
      )
      .subscribe(({ url, entryPoint }) => {
        if (entryPoint) {
          return this.store.dispatch(
            fromRouterActions.Go({
              path: [entryPoint]
            })
          );
        }

        if (url && !url.includes('add-vehicle')) {
          return this.store.dispatch(
            fromRouterActions.Go({
              path: [url]
            })
          );
        }

        this.store.dispatch(
          fromRouterActions.Go({
            path: ['overview']
          })
        );
      });
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();

    if (this.policySub) {
      this.policySub.unsubscribe();
    }
  }
  private handleError(componentReference) {
    this.store.dispatch(VehicleFeatureActions.addVehicleBindReset());
    this.hasError = false;
    if (_get(componentReference, 'errorEvent')) {
      //Below will subscribe to the error emitter
      componentReference.errorEvent
        .pipe(takeUntil(this.stop$))
        .subscribe(({ hasError, errorMessage } = { hasError: true, errorMessage: '' }) => {
          this.hasError = hasError;
          if (errorMessage) {
            this.errorList = [
              {
                errorMessage: errorMessage,
                errorLevel: ErrorLevels.HIGH
              }
            ];
          }
        });
    }
  }

  /**
   * @description Function which fetches the error from the copy service and returns it in the error obj format.
   */
  private getPoliciesError(copyService: CopyService): ErrorObj {
    return {
      errorMessage: copyService.getCopy('policy.addVehicleErrorMessage'),
      errorLevel: ErrorLevels.HIGH
    };
  }
}
